import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Helmet, HelmetProvider } from "react-helmet-async";
import NavigationBar from "./components/NavigationBar";
import { Footer } from "./components/Footer";
import useScrollToTopOnRouteChange from "@/hooks/useScrollToTopOnRouteChange";
import { Loader2 } from "lucide-react"; 


// Lazy-loaded pages for route-based code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const StoryGen = lazy(() => import("./pages/storygen"));
const PetNameGen = lazy(() => import("./pages/petname"));
const QuickFix = lazy(() => import("./pages/quickfix"));
const TemplateGen = lazy(() => import("./pages/template"));
const Profile = lazy(() => import("./pages/profile"));
const Login = lazy(() => import("./pages/login"));
const Signup = lazy(() => import("./pages/signup"));
const Privacy = lazy(() => import("./pages/privacy"));
const Terms = lazy(() => import("./pages/terms"));
const ToneShifter = lazy(() => import("./pages/toneshift"));
const Contact = lazy(() => import("./pages/contact"));
const AgendaGen = lazy(() => import("./pages/agenda"));
const PricingTier = lazy(() => import("./pages/pricing"));
const QuizGenerator = lazy(() => import("./pages/quiz"));
const FeedbackPage = lazy(() => import("./pages/admin/feedback"));
const Analytics = lazy(() => import("./pages/admin/analytics"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
        <Helmet>
            {/* Basic SEO */}
            <meta charSet="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link rel="canonical" href="https://generativetoolbox.com" />
            <meta name="robots" content="index, follow" />

            {/* Global Primary Meta Tags */}
            <title>Generative AI Toolbox | Explore the Best AI Tools for Creativity & Productivity</title>
            <meta name="description" content="Try the best AI-powered tools to supercharge your creativity and productivity—100% free!" />

            {/* Global Open Graph Tags */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://generativetoolbox.com" />
            <meta property="og:title" content="Generative AI Toolbox - Discover AI Tools for Creativity & Productivity!" />
            <meta property="og:description" content="Try the best AI-powered tools to supercharge your workflow—100% free!" />
            {/* Desktop image */}
            <meta property="og:image" content="https://qflkyfnbpwwzcqocwpmg.supabase.co/storage/v1/object/public/seo-images/Default/og-generative-ai-toolbox-1200x630.png" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            {/* Mobile image */}
            <meta property="og:image" content="https://qflkyfnbpwwzcqocwpmg.supabase.co/storage/v1/object/public/seo-images/Default/og-generative-ai-toolbox-600x314.png" />
            <meta property="og:image:width" content="600" />
            <meta property="og:image:height" content="314" />
            <meta property="og:image:alt" content="AI-powered tools for creativity, productivity, and innovative solutions." />
            <meta property="og:site_name" content="Generative AI Toolbox" />
            <meta property="og:locale" content="en_US" />

            {/* Global Twitter Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@generativetoolx" />
            <meta name="twitter:creator" content="@techswq" />
            <meta name="twitter:url" content="https://generativetoolbox.com" />
            <meta name="twitter:title" content="Generative AI Toolbox - Discover AI Tools for Creativity & Productivity!" />
            <meta name="twitter:description" content="Try the best AI-powered tools to supercharge your workflow—100% free!" />
            {/* Desktop image */}
            <meta name="twitter:image" content="https://qflkyfnbpwwzcqocwpmg.supabase.co/storage/v1/object/public/seo-images/Default/twitter-generative-ai-1200x630.png" />
            {/* Mobile image via media query */}
            <meta name="twitter:image:src" media="(max-width: 600px)" content="https://qflkyfnbpwwzcqocwpmg.supabase.co/storage/v1/object/public/seo-images/Default/twitter-generative-ai-600x314.png" />
            <meta name="twitter:image:alt" content="AI-powered tools for creativity, productivity, and innovative solutions." />
          </Helmet>

          <div className="min-h-screen flex flex-col">
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop>
                <NavigationBar />
                <Suspense 
                  fallback={
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="animate-spin h-8 w-8 text-muted-foreground" />
                    </div>
                  }
                  >
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/storygen" element={<StoryGen />} />
                    <Route path="/petname" element={<PetNameGen />} />
                    <Route path="/quickfix" element={<QuickFix />} />
                    <Route path="/template" element={<TemplateGen />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/toneshift" element={<ToneShifter />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/agenda" element={<AgendaGen />} />
                    <Route path="/pricing" element={<PricingTier />} />
                    <Route path="/quiz" element={<QuizGenerator />} />
                    <Route path="/admin/feedback" element={<FeedbackPage />} />
                    <Route path="/admin/analytics" element={<Analytics />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <Footer />
              </ScrollToTop>
            </BrowserRouter>
          </div>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

const ScrollToTop = ({ children }: { children: React.ReactNode }) => {
  useScrollToTopOnRouteChange();
  return <>{children}</>;
};

export default App;
